import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Clock, Phone, MessageCircle, Volume2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = "33745522450";

// Notification sound
const playNotificationSound = () => {
  try {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQQ/eNbMmGYSD1WXvLiLXCUdWoCqrpBrMiNIW4GShXpjTkdTXGJpaHB0cnRwbGVfWVJNTEs+NjQvKSkoKCooKykqLDA0Nzs/');
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (e) {
    console.log('Sound not supported');
  }
};

// Format time remaining
const formatTimeRemaining = (ms) => {
  const minutes = Math.floor(ms / 60000);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}min`;
  }
  return `${minutes} min`;
};

// Notification Component
export const NotificationToast = ({ notification, onDismiss, onAction }) => {
  const [isVisible, setIsVisible] = useState(true);
  
  useEffect(() => {
    // Auto dismiss after 30 seconds for non-urgent
    if (notification.priority !== 'urgent') {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onDismiss(notification.id), 300);
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [notification, onDismiss]);
  
  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => onDismiss(notification.id), 300);
  };
  
  const bgColor = notification.priority === 'urgent' 
    ? 'bg-red-500' 
    : notification.priority === 'warning'
      ? 'bg-amber-500'
      : 'bg-purple-600';
  
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, x: 300, scale: 0.9 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 300, scale: 0.9 }}
          className={`${bgColor} text-white rounded-xl shadow-2xl p-4 max-w-sm w-full`}
        >
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              {notification.type === 'call_reminder' ? (
                <Phone className="w-5 h-5" />
              ) : (
                <Bell className="w-5 h-5" />
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-bold text-sm">{notification.title}</h4>
                <button
                  onClick={handleDismiss}
                  className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <p className="text-sm text-white/90">{notification.message}</p>
              
              {notification.lead && (
                <div className="mt-2 p-2 bg-white/10 rounded-lg">
                  <p className="font-bold text-sm">{notification.lead.restaurant}</p>
                  <p className="text-xs text-white/70">{notification.lead.owner}</p>
                </div>
              )}
              
              {/* Actions */}
              {notification.actions && (
                <div className="mt-3 flex gap-2">
                  {notification.actions.map((action, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        onAction && onAction(notification, action);
                        handleDismiss();
                      }}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${
                        action.primary 
                          ? 'bg-white text-gray-800 hover:bg-gray-100' 
                          : 'bg-white/20 hover:bg-white/30'
                      }`}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Notification Container
export const NotificationContainer = ({ notifications, onDismiss, onAction }) => {
  return (
    <div className="fixed top-4 right-4 z-[100] space-y-3 max-h-screen overflow-hidden">
      <AnimatePresence>
        {notifications.map((notification) => (
          <NotificationToast
            key={notification.id}
            notification={notification}
            onDismiss={onDismiss}
            onAction={onAction}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

// Call Reminder Hook
export const useCallReminders = (leads, onNotify) => {
  const [remindedCalls, setRemindedCalls] = useState(new Set());
  
  const checkReminders = useCallback(() => {
    const now = new Date();
    
    leads.forEach(lead => {
      if (!lead.scheduled_call) return;
      
      const callTime = new Date(lead.scheduled_call.datetime);
      const timeDiff = callTime - now;
      
      // Skip if in the past
      if (timeDiff < 0) return;
      
      const callId = `${lead.id}-${lead.scheduled_call.datetime}`;
      
      // 1 hour reminder
      const oneHourMs = 60 * 60 * 1000;
      const oneHourKey = `${callId}-1h`;
      if (timeDiff <= oneHourMs && timeDiff > oneHourMs - 60000 && !remindedCalls.has(oneHourKey)) {
        setRemindedCalls(prev => new Set([...prev, oneHourKey]));
        playNotificationSound();
        onNotify({
          id: oneHourKey,
          type: 'call_reminder',
          priority: 'warning',
          title: '📅 Appel dans 1 heure',
          message: `N'oublie pas ton appel avec ${lead.restaurant}`,
          lead,
          actions: [
            { label: 'Ouvrir WhatsApp', primary: true, action: 'whatsapp' },
            { label: 'OK', action: 'dismiss' }
          ]
        });
        
        // Send browser notification
        if (Notification.permission === 'granted') {
          new Notification(`📅 Appel dans 1h - ${lead.restaurant}`, {
            body: `Prépare-toi pour l'appel avec ${lead.owner || 'le contact'}`,
            icon: '/logo192.png',
            tag: oneHourKey
          });
        }
      }
      
      // 10 min reminder
      const tenMinMs = 10 * 60 * 1000;
      const tenMinKey = `${callId}-10m`;
      if (timeDiff <= tenMinMs && timeDiff > tenMinMs - 60000 && !remindedCalls.has(tenMinKey)) {
        setRemindedCalls(prev => new Set([...prev, tenMinKey]));
        playNotificationSound();
        onNotify({
          id: tenMinKey,
          type: 'call_reminder',
          priority: 'urgent',
          title: '🚨 Appel dans 10 minutes !',
          message: `Appel imminent avec ${lead.restaurant}`,
          lead,
          actions: [
            { label: 'Appeler maintenant', primary: true, action: 'whatsapp' },
            { label: 'OK', action: 'dismiss' }
          ]
        });
        
        // Send browser notification
        if (Notification.permission === 'granted') {
          new Notification(`🚨 Appel dans 10 min - ${lead.restaurant}`, {
            body: `C'est presque l'heure ! Appelle ${lead.owner || 'le contact'}`,
            icon: '/logo192.png',
            tag: tenMinKey,
            requireInteraction: true
          });
        }
      }
    });
  }, [leads, remindedCalls, onNotify]);
  
  // Check every minute
  useEffect(() => {
    checkReminders();
    const interval = setInterval(checkReminders, 60000);
    return () => clearInterval(interval);
  }, [checkReminders]);
  
  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);
};

// WhatsApp Template for scheduled call
export const getCallReminderWhatsAppLink = (lead, reminderType) => {
  const cleanPhone = lead.phone?.replace(/[^0-9]/g, '') || WHATSAPP_NUMBER;
  
  const messages = {
    '1h': `Salut ! Je te rappelle notre appel prévu dans 1h pour parler de ${lead.restaurant}. À tout de suite !`,
    '10m': `Hey ! C'est l'heure de notre appel pour ${lead.restaurant}. Je t'appelle dans 2 min ?`,
    'now': `Salut ! C'est l'heure de notre appel pour ${lead.restaurant}. Tu es dispo ?`
  };
  
  const message = encodeURIComponent(messages[reminderType] || messages.now);
  return `https://wa.me/${cleanPhone}?text=${message}`;
};

// Notification when call is scheduled
export const getScheduledCallNotification = (lead, datetime) => {
  const callDate = new Date(datetime);
  return {
    id: `scheduled-${lead.id}-${Date.now()}`,
    type: 'call_scheduled',
    priority: 'info',
    title: '📅 Appel programmé',
    message: `RDV avec ${lead.restaurant} le ${callDate.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    })}`,
    lead,
    actions: [
      { label: 'Copier rappel', primary: false, action: 'copy_reminder' },
      { label: 'OK', action: 'dismiss' }
    ]
  };
};

// Get WhatsApp template for manual reminder
export const getWhatsAppReminderTemplate = (lead, datetime) => {
  const callDate = new Date(datetime);
  const dateStr = callDate.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit'
  });
  
  return `📅 Nouveau RDV programmé\nRestaurant : ${lead.restaurant}\nContact : ${lead.owner || 'N/A'}\nHeure : ${dateStr}`;
};

export default NotificationContainer;
